import type { RemoveIndexSignature } from 'src/types/removeIndexSignature';

import { ComposerTranslation, LocaleMessageValue, VueMessageType } from 'vue-i18n';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Messaging, deleteToken, getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useAlarmStore } from 'src/stores/alarm';
import { AlarmActionOutcome, outcomeFromAlarmActionResult } from 'src/stores/alarm/alarmActionOutcome';
import { useAuthenticationStore } from 'src/stores/authentication';
import { useMenuStore } from 'src/stores/menu';
import { useUserStore } from 'src/stores/user';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { openDetails } from 'src/components/workspace/menu/functions';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';

import { CommandBus } from 'src/util/command/CommandBus';
import { translatedModuleName } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { getBrowserName, getPlatformName, isScreenGtSm } from 'src/util/platform';
import { QueryStore } from 'src/util/query/QueryStore';

type FcmNotificationData = {
	aggregateId?: string;
	instanceName?: string;
};

type AggregateIdPayload = {
	moduleName: string;
};

const isFcmNotificationData = (value: unknown): value is FcmNotificationData => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const candidate = value as Record<string, unknown>;

	return (
		(candidate.aggregateId === undefined || typeof candidate.aggregateId === 'string') &&
		(candidate.instanceName === undefined || typeof candidate.instanceName === 'string')
	);
};

const parseAggregateIdPayload = (aggregateId: string): AggregateIdPayload | null => {
	try {
		const parsedValue = JSON.parse(aggregateId) as unknown;

		if (
			typeof parsedValue === 'object' &&
			parsedValue !== null &&
			'moduleName' in parsedValue &&
			typeof parsedValue.moduleName === 'string'
		) {
			return {
				moduleName: parsedValue.moduleName,
			};
		}
	} catch {
		return null;
	}

	return null;
};

const NOTIFICATION_CLICK_MESSAGE_TYPE = 'NOTIFICATION_CLICK';

type NotificationClickMessage = {
	data: unknown;
	type: typeof NOTIFICATION_CLICK_MESSAGE_TYPE;
};

const isNotificationClickMessage = (value: unknown): value is NotificationClickMessage => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const candidate = value as Record<string, unknown>;

	return candidate.type === NOTIFICATION_CLICK_MESSAGE_TYPE && 'data' in candidate;
};

const WEB_PUSH_SERVICE_WORKER_URL = '/custom-service-worker.js';
const JAVASCRIPT_CONTENT_TYPE_FRAGMENT = 'javascript';

type BackgroundFetchTaskLifecycle = {
	finished: boolean;
};

export class FcmService {
	private readonly commandBus: CommandBus;
	private readonly eventBus: EventBus;

	private readonly backgroundFetchTaskLifecycles = new Map<string, BackgroundFetchTaskLifecycle>();
	private firebase!: FirebaseApp;
	private messaging!: Messaging;
	private readonly queryStore: QueryStore;
	private readonly router: Router;

	private serviceWorkerRegistration!: ServiceWorkerRegistration;
	private splitViewEnabled: boolean;
	protected readonly route: RouteLocationNormalizedLoaded;

	private readonly runtimeStore: RuntimeStoreContext;

	private readonly t: ComposerTranslation<
		{
			[p: string]: LocaleMessageValue<VueMessageType>;
		},
		string,
		RemoveIndexSignature<{
			[p: string]: LocaleMessageValue<VueMessageType>;
		}>,
		never,
		string,
		string
	>;

	constructor(
		t: ComposerTranslation<
			{
				[p: string]: LocaleMessageValue<VueMessageType>;
			},
			string,
			RemoveIndexSignature<{
				[p: string]: LocaleMessageValue<VueMessageType>;
			}>,
			never,
			string,
			string
		>,
		runtimeStore: RuntimeStoreContext,
		router: Router,
		route: RouteLocationNormalizedLoaded,
		queryStore: QueryStore,
		commandBus: CommandBus,
		eventBus: EventBus,
		splitViewEnabled: boolean
	) {
		this.t = t;
		this.runtimeStore = runtimeStore;
		this.router = router;
		this.route = route;
		this.queryStore = queryStore;
		this.commandBus = commandBus;
		this.eventBus = eventBus;
		this.splitViewEnabled = splitViewEnabled;
	}

	private checkForCapacitorPushPermissionsAndRegisterIfGranted() {
		void PushNotifications.checkPermissions().then((res) => {
			if (res.receive !== 'granted') {
				void PushNotifications.requestPermissions().then((res) => {
					if (res.receive === 'denied') {
						/* empty */
					} else {
						this.registerCapacitorPush();
					}
				});
			} else {
				this.registerCapacitorPush();
			}
		});

		void Badge.isSupported().then((isSupported) => {
			if (isSupported) {
				void Badge.checkPermissions().then((res) => {
					if (res.display == 'granted') {
						void Badge.requestPermissions().then((res) => {
							if (res.display === 'denied') {
								/* empty */
							}
						});
					}
				});
			}
		});

		void LocalNotifications.checkPermissions().then((res) => {
			if (res.display === 'denied') {
				void LocalNotifications.requestPermissions().then((res) => {
					if (res.display === 'denied') {
						return;
					}
				});
			}
		});
	}

	private async fetchTheUnfinishedElementCountList() {
		const menuStore = useMenuStore();
		await menuStore.fetchUnfinishedElementCountList({
			queryStore: this.queryStore,
		});
	}

	private async fetchAlarms(): Promise<AlarmActionOutcome> {
		const res = await LocalNotifications.checkPermissions();
		if (res.display === 'granted') {
			const result = await useAlarmStore().backgroundFetchAlarms({
				browser: getBrowserName(),
				localNotifications: LocalNotifications,
				platform: getPlatformName(),
				queryStore: this.queryStore,
				t: this.t,
			});
			return outcomeFromAlarmActionResult(result);
		}

		return 'noop';
	}

	private async finishBackgroundFetch(taskId: string, lifecycle: BackgroundFetchTaskLifecycle): Promise<void> {
		try {
			const outcome = await this.fetchAlarms();
			if (outcome !== 'failure') {
				await this.fetchTheUnfinishedElementCountList();
			}
		} catch (error) {
			console.error('Background fetch failed:', error);
		} finally {
			await this.finishBackgroundFetchTaskOnce(taskId, lifecycle);
		}
	}

	private async finishBackgroundFetchTimeout(taskId: string): Promise<void> {
		const lifecycle = this.backgroundFetchTaskLifecycles.get(taskId) ?? { finished: false };
		this.backgroundFetchTaskLifecycles.set(taskId, lifecycle);
		try {
			return;
		} finally {
			await this.finishBackgroundFetchTaskOnce(taskId, lifecycle);
		}
	}

	private async finishBackgroundFetchTaskOnce(
		taskId: string,
		lifecycle: BackgroundFetchTaskLifecycle
	): Promise<void> {
		if (lifecycle.finished) {
			return;
		}

		lifecycle.finished = true;
		await BackgroundFetch.finish(taskId);
	}

	private startBackgroundFetchLifecycle(taskId: string): BackgroundFetchTaskLifecycle {
		const lifecycle = { finished: false };
		this.backgroundFetchTaskLifecycles.set(taskId, lifecycle);
		return lifecycle;
	}

	private openNotificationTarget(data: unknown) {
		if (!isFcmNotificationData(data) || data.aggregateId === undefined || data.instanceName === undefined) {
			return;
		}

		const aggregateIdPayload = parseAggregateIdPayload(data.aggregateId);

		if (aggregateIdPayload === null) {
			return;
		}

		const authenticationStore = useAuthenticationStore();
		const { aggregateId, instanceName } = data;

		const currentInstanceName = authenticationStore.activeInstanceAuthenticationConfigurationName;
		if (currentInstanceName.toLowerCase() !== instanceName.toLowerCase()) {
			const allInstanceConfigurations =
				authenticationStore.allInstanceAuthenticationConfigurations as InstanceAuthenticationConfiguration[];
			const existingCaseSensitiveInstance = allInstanceConfigurations.find((instanceConfiguration) => {
				return instanceConfiguration.name.toLowerCase() === instanceName.toLowerCase();
			});

			if (existingCaseSensitiveInstance !== undefined) {
				authenticationStore.setActiveInstanceAuthenticationName(existingCaseSensitiveInstance.name);
				void this.navigate('index.html').then(() => {
					openDetails(
						this.router,
						this.route,
						this.runtimeStore,
						this.eventBus,
						isScreenGtSm() && this.splitViewEnabled,
						translatedModuleName(this.t, aggregateIdPayload.moduleName),
						aggregateId,
						aggregateIdPayload.moduleName,
						'module'
					);
				});
			}
		} else {
			openDetails(
				this.router,
				this.route,
				this.runtimeStore,
				this.eventBus,
				isScreenGtSm() && this.splitViewEnabled,
				translatedModuleName(this.t, aggregateIdPayload.moduleName),
				aggregateId,
				aggregateIdPayload.moduleName,
				'module'
			);
		}
	}

	private registerCapacitorPush() {
		const userStore = useUserStore();
		void PushNotifications.register();

		void PushNotifications.addListener('registration', (token: Token) => {
			userStore.registerFcmDeviceToken({
				commandBus: this.commandBus,
				fcmDeviceToken: token,
			});
		});

		void PushNotifications.addListener('registrationError', (error: unknown) => {
			console.error('Error on push registration:', error);
		});

		void PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
			this.openNotificationTarget(notification.notification.data);
		});
	}

	async checkForExistingPushSubscriptionAndSubscribeIfNecessary() {
		if (this.serviceWorkerSupported() && this.pushNotificationSupported()) {
			const serviceWorkerRegistration = await this.resolveWebPushServiceWorkerRegistration();

			if (serviceWorkerRegistration === null) {
				return;
			}

			this.serviceWorkerRegistration = serviceWorkerRegistration;

			void this.serviceWorkerRegistration.pushManager.getSubscription().then(() => {
				this.registerTheOnMessageHandlerForDesktopNotifications();
				this.createWebPushSubscription();
			});
		}
	}

	checkForWebPushPermissionsAndRegisterIfGranted() {
		if (this.serviceWorkerSupported() && this.pushNotificationSupported()) {
			void Notification.requestPermission((result) => {
				if (result == 'granted') {
					void this.checkForExistingPushSubscriptionAndSubscribeIfNecessary();
				}
			});
		}
	}

	createWebPushSubscription() {
		const userStore = useUserStore();

		getToken(this.messaging, {
			serviceWorkerRegistration: this.serviceWorkerRegistration,
			vapidKey: 'BPsKrXZjJHbgtuZM-lWtgodaQpe7PohvgDry6qysv5QSqkXbBik_YHMnEiCQ1c6rqBcYD7dP9AxQwAKId6KTqP0',
		})
			.then((currentToken) => {
				if (currentToken) {
					userStore.registerFcmDeviceToken({
						commandBus: this.commandBus,

						fcmDeviceToken: { value: currentToken },
					});
				}
			})
			.catch((err) => {
				console.error('An error occurred while retrieving the push token.', err);
			});
	}

	async initBackgroundFetch() {
		try {
			const status = await BackgroundFetch.configure(
				{
					minimumFetchInterval: 15,
					requiredNetworkType: BackgroundFetch.NETWORK_TYPE_ANY,
					requiresBatteryNotLow: false,
					requiresCharging: false,
					requiresDeviceIdle: false,
					requiresStorageNotLow: false,
				},
				(taskId) => {
					const lifecycle = this.startBackgroundFetchLifecycle(taskId);
					void this.finishBackgroundFetch(taskId, lifecycle).catch((error) => {
						console.error('Failed to finish background fetch:', error);
					});
				},
				(taskId) => {
					void this.finishBackgroundFetchTimeout(taskId).catch((error) => {
						console.error('Failed to finish background fetch timeout:', error);
					});
				}
			);

			if (status !== BackgroundFetch.STATUS_AVAILABLE) {
				if (status === BackgroundFetch.STATUS_DENIED) {
					console.warn(
						'The user explicitly disabled background behavior for this app or for the whole system.'
					);
				} else if (status === BackgroundFetch.STATUS_RESTRICTED) {
					console.warn('Background updates are unavailable and the user cannot enable them again.');
				}
			}
		} catch (e) {
			console.error(e);
		}
	}

	initPush() {
		if (Capacitor.getPlatform() !== 'web') {
			this.checkForCapacitorPushPermissionsAndRegisterIfGranted();
			void this.initBackgroundFetch();
		} else {
			const firebaseConfig = {
				apiKey: 'AIzaSyAPTb52fvZxgx4lCgGUdc_GnofRSpEmR1g',
				appId: '1:486270531258:web:a814c357f9b4f6f9f43483',
				authDomain: 'infra-struktur.firebaseapp.com',
				measurementId: 'G-45572JTEGD',
				messagingSenderId: '486270531258',
				projectId: 'infra-struktur',
				storageBucket: 'infra-struktur.appspot.com',
			};

			this.firebase = initializeApp(firebaseConfig);

			this.messaging = getMessaging(this.firebase);

			this.checkForWebPushPermissionsAndRegisterIfGranted();
			this.registerTheServiceWorkerNotificationClickListener();
		}
	}

	public navigate(url: string): Promise<void> {
		return new Promise((resolve) => {
			document.location.href = url;
			resolve();
		});
	}

	pushNotificationSupported(): boolean {
		return 'PushManager' in window;
	}

	registerTheOnMessageHandlerForDesktopNotifications() {
		onMessage(this.messaging, (payload) => {
			void this.serviceWorkerRegistration.showNotification(<string>payload.notification?.title, {
				body: payload.notification?.body,
				data: payload.data,
				tag: payload.data?.aggregateId,
			});
		});
	}

	private registerTheServiceWorkerNotificationClickListener() {
		if (!this.serviceWorkerSupported()) {
			return;
		}

		navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
			const message: unknown = event.data;

			if (!isNotificationClickMessage(message)) {
				return;
			}

			this.openNotificationTarget(message.data);
		});
	}

	private async resolveWebPushServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
		const existingRegistration = await navigator.serviceWorker.getRegistration('/');
		const existingScriptUrl =
			existingRegistration?.active?.scriptURL ??
			existingRegistration?.waiting?.scriptURL ??
			existingRegistration?.installing?.scriptURL ??
			'';

		if (existingRegistration && existingScriptUrl.endsWith(WEB_PUSH_SERVICE_WORKER_URL)) {
			return navigator.serviceWorker.ready;
		}

		try {
			const response = await fetch(WEB_PUSH_SERVICE_WORKER_URL, {
				cache: 'no-store',
				method: 'HEAD',
			});
			const contentType = response.headers.get('content-type') ?? '';

			if (!response.ok || !contentType.includes(JAVASCRIPT_CONTENT_TYPE_FRAGMENT)) {
				return null;
			}
		} catch (error) {
			console.error('FCM: Failed to probe the web push service worker.', error);
			return null;
		}

		await navigator.serviceWorker.register(WEB_PUSH_SERVICE_WORKER_URL, { updateViaCache: 'none' });
		return navigator.serviceWorker.ready;
	}

	serviceWorkerSupported(): boolean {
		return 'serviceWorker' in navigator;
	}

	public setSplitViewEnabled(state: boolean) {
		this.splitViewEnabled = state;
	}

	unsubscribePush() {
		const userStore = useUserStore();

		const currentToken = userStore.fcmDeviceToken;
		const currentTokenValue = typeof currentToken === 'string' ? currentToken : currentToken.value;

		void userStore
			.deleteUserFcmDeviceToken({
				commandBus: this.commandBus,
				fcmDeviceToken: { value: currentTokenValue },
			})
			.then(() => {
				try {
					void navigator.serviceWorker.ready.then((registration) => {
						if (registration.active) {
							registration.active.postMessage({
								type: 'UNREGISTER_FIREBASE',
							});
						}
					});
				} catch (e) {
					console.error(e);
				}
			});

		void deleteToken(this.messaging);
	}
}
